import React, { useEffect, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ContactFormFields } from '@customTypes/ContactFormFields'
import { TreatmentInfo as TreatmentInfoType } from '@customTypes/TreatmentInfo'
import useContactFormValidation from '@hooks/useContactFormValidation'
import { yupResolver } from '@hookform/resolvers/yup'
import Page from '@components/Page'
import TreatmentInfo from '@components/TreatmentInfo'
import FormError from '@components/FormError'

import ContactDetails from './components/ContactDetails'
import Legal from './components/Legal'

import useStyles from './contactScreen.styles'

import { BookingFor as BookingForType } from '@customTypes/BookingFor'
import PatientDetails from './components/PatientDetails'
import Header from '@components/Header'
import Footer from '@components/Footer'
import InsuranceHolder from './components/InsuranceHolder'
import BookingFor from './components/BookingFor'
import { useChoiceState } from '@context/ChoiceContext'
import Input from '@components/Input'
import { Button } from '@components/Button/Button'
import { ErrorBoundary } from '@components/ErrorBoundary/ErrorBoundary'
import Spinner from '@components/Spinner'
import { Alert } from '@mui/material'
import { ScrollableAlert, ScrollableAlertInteraction } from '@components/ScrollableAlert/ScrollableAlert'
import PageSidebar from '@components/Page/PageSidebar'
import { useIsDesktopUp } from 'src/config/mediaQueries'
import AppointmentInfo from '../Appointment/components/AppointmentInfo'

export interface ContactScreenProps {
  error?: string
  onBackClicked: () => void
  onNextClicked: (values: ContactFormFields, token?: string) => void
  onFormChange: (values: ContactFormFields) => void
  initialValues: ContactFormFields
  treatmentInfo: TreatmentInfoType
  isProcessing?: boolean
  nonModalEmbed?: boolean
  shouldVerifyPhoneNumber: boolean
}

const ContactScreen = ({
  error = '',
  onBackClicked,
  onNextClicked,
  initialValues,
  onFormChange,
  treatmentInfo,
  isProcessing,
  shouldVerifyPhoneNumber,
}: ContactScreenProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const { isManualReviewRequired } = useChoiceState()
  const validationSchema = useContactFormValidation({ isManualReviewRequired })

  const methods = useForm<ContactFormFields>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  })

  const {
    watch,
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    clearErrors,
  } = methods

  const onSubmit = () => {
    const values = getValues()

    onFormChange(values)
    onNextClicked(values)
  }

  useEffect(() => {
    return () => {
      onFormChange(getValues())
    }
  }, [getValues, onFormChange])

  const canSubmit = !Object.keys(errors).length && !isProcessing
  const visualHint = treatmentInfo?.treatment?.visualHint

  const errorMessage = error && t(error, { defaultValue: t('api_errors.generic') })
  const isBookingForAnotherPerson = watch('bookingFor') === BookingForType.BOOKING_FOR_OTHER

  const alertRef = useRef<ScrollableAlertInteraction>(null)
  const isDesktopUp = useIsDesktopUp()


  useEffect(() => {
    if (errorMessage && alertRef.current) {
      alertRef.current.show()
    }
  }, [errorMessage])

  useEffect(() => {
    if (isBookingForAnotherPerson) {
      clearErrors('birthdate')
    } else {
      clearErrors(['patient_firstName', 'patient_lastName', 'patient_birthdate'])
    }
  }, [isBookingForAnotherPerson, clearErrors])

  const body = (
    <ErrorBoundary fallback={<div />} additionalDebugData="ContactScreen - Body">
      <FormProvider {...methods}>
        <form id="hook-form" noValidate>
          <div className={classes.formSections}>
            {!isDesktopUp &&
              <div className={clsx(classes.formSection, classes.sectionTreatment)}>
                <TreatmentInfo data={treatmentInfo} variant="contactScreen" />
              </div>
            }

            {visualHint && (
              <Alert className={classes.restrictWidth} severity="info">
                {t(visualHint)}
              </Alert>
            )}

            <div className={clsx(classes.formSection, classes.sectionContactDetails)}>
              <h4 className={classes.bookingForHeader}>{t('contact_page_sub_headline')}</h4>
              <BookingFor />
              <h4 className={classes.bookingForHeader}>{t('patient')}</h4>
              {isBookingForAnotherPerson && (
                <>
                  <PatientDetails />
                  <h4 className={classes.bookingForHeader}>{t('contact_person')}</h4>
                </>
              )}
              <ContactDetails isBookingForAnotherPerson={isBookingForAnotherPerson} />
              {isBookingForAnotherPerson && (
                <>
                  <h4 className={classes.bookingForHeader}>{t('insurance_holder')}</h4>
                  <InsuranceHolder />
                </>
              )}
              {isManualReviewRequired && (
                <Input
                  className={classes.inputContainer}
                  type="text"
                  data-test="reasonForVisit"
                  placeholder={t('reason_for_visit')}
                  {...register('reasonForVisit')}
                  info={
                    errors.reasonForVisit ? (
                      <FormError message={errors.reasonForVisit.message} />
                    ) : (
                      t('reason_for_visit_info')
                    )
                  }
                />
              )}
            </div>

            {errorMessage && (
              <ScrollableAlert ref={alertRef} className={classes.restrictWidth} severity="error">
                {errorMessage}
              </ScrollableAlert>
            )}
          </div>
        </form>
      </FormProvider>
    </ErrorBoundary>
  )

  const PageComponent = isDesktopUp ? PageSidebar : Page

  return (
    <PageComponent
      header={
        <ErrorBoundary fallback={<div />} additionalDebugData="ContactScreen - Header">
          <Header onBackClick={onBackClicked} hideEmptyHeadingSpacing/>
        </ErrorBoundary>
      }
      body={
        <div>
          {isProcessing && <Spinner showOverlay size="large" />}
          {body}
        </div>
      }
      sideBar={isDesktopUp && <AppointmentInfo hidePractitionerChangeButton />}
      footer={
        <ErrorBoundary fallback={<div />} additionalDebugData="ContactScreen - Footer">
          <Footer
            beforeButton={<Legal className={clsx(classes.restrictWidth, classes.legal)} />}
            button={
              <Button
                onClick={handleSubmit(onSubmit)}
                fullSize
                type="submit"
                text={shouldVerifyPhoneNumber ? t('new_patient_flow_contact_form_CTA_text') : t('book_appointment')}
                data-test="buttonSubmit"
                loading={!!isProcessing}
                disabled={!canSubmit}
                additionalDebugData="ContactScreen CTA"
              />
            }
          />
        </ErrorBoundary>
      } />
  )
}

export default ContactScreen
